import React, { FC, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { GroupRule, GroupRuleSearchPattern, GroupRuleSearchPatternType, ResponseData, GroupRuleRequest } from "../../../../../models";
import { postSetting } from "../../../../../services/post.data.service";
import { putSetting } from "../../../../../services/put.data.service";
import SelectInput from "../../shared/fields/selectInput";
import ToggleInput from "../../shared/fields/toggleInput";
import SettingsFormInfo from "../../shared/settingsFormInfo";
import SettingsTitle from "../../shared/settingsTitle";
import { getDropdownData, getGroupOptionsForGroupRules } from "../../../../../services/get.data.service";
import { SelectOption } from "../../../../../models/selectOption";
import GroupRuleSearchPatterns from "./groupRuleSearchPatterns";
import TextareaInput from "../../shared/fields/textareaInput";
import SettingsSubmitButton from "../../shared/settingsSubmitButton";
import TextInput from "../../shared/fields/textInput";

type Props = {
  existingGroupRule?: GroupRule;
  isDuplicate: (value: string) => boolean;
  load: () => void;
};

const emptyPattern = (): GroupRuleSearchPattern => ({
  isCaseSensitive: false,
  isRegex: false,
  pattern: "",
  searchPatternType: GroupRuleSearchPatternType.AttachmentName,
});

const GroupRuleForm: FC<Props> = ({ existingGroupRule, isDuplicate, load }) => {
  const [redirect, setRedirect] = useState<boolean>(false);
  const [groupOptions, setGroupOptions] = useState<SelectOption[]>([]);
  const [emailAccountOptions, setEmailAccountOptions] = useState<SelectOption[]>([]);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [emailAccountId, setEmailAccountId] = useState<number | null>(null);
  const [name, setName] = useState<string>("");
  const [mustMatchAllPatterns, setMustMatchAllPatterns] = useState<boolean>(false);
  const [searchPatterns, setSearchPatterns] = useState<GroupRuleSearchPattern[]>([]);
  const [fromAddresses, setFromAddresses] = useState<string>("");
  const [errors, setErrors] = useState<string[]>([]);

  const emailRegEx = RegExp(/^([\w-.]+|\*)@([\w-]+\.)+[\w-]{2,}$/);

  useEffect(() => {
    if (existingGroupRule) {
      setName(existingGroupRule.name);
      setEmailAccountId(existingGroupRule.emailAccountId);
      setGroupId(existingGroupRule.groupId);
      setMustMatchAllPatterns(existingGroupRule.mustMatchAllPatterns);
      setFromAddresses(existingGroupRule.fromAddresses?.join("; ") || "");
      setSearchPatterns(existingGroupRule.searchPatterns?.length ? existingGroupRule.searchPatterns : []);
    } else {
      setSearchPatterns([emptyPattern()]);
    }
    getGroupOptionsForGroupRules().then((response) => {
      setGroupOptions(response.data);
    });
    getDropdownData("emailaccounts").then((response) => {
      setEmailAccountOptions(response.data);
    });
  }, [existingGroupRule]);

  const validate = (): string[] => {
    const validationErrors: string[] = [];

    if (name.trim().length === 0) {
      validationErrors.push("Name is required");
    } else if (isDuplicate(name.trim())) {
      validationErrors.push("Name has already been used");
    }

    if (emailAccountId === null) {
      validationErrors.push("Polling Address is required");
    }

    if (groupId === null) {
      validationErrors.push("Group is required");
    }

    const hasAnyPattern = searchPatterns.some((pattern) => pattern.pattern.trim().length > 0);
    if (!hasAnyPattern && fromAddresses.trim().length === 0) {
      validationErrors.push("At least one Search Pattern or From Address is required");
    }

    if (fromAddresses.split(";").some((addr) => addr.trim().length > 0 && !emailRegEx.test(addr.trim()))) {
      validationErrors.push("One or more From Addresses are not valid email addresses");
    }

    return validationErrors;
  };

  const handleSaveResponse = (result: ResponseData<GroupRuleRequest>): void => { 
    if (result.success) {
      load();
      setRedirect(true);
    } else {
      setErrors([`Failed to update. ${result.message || result}`]);
    }
  };

  const addPattern = (): void => {
    setSearchPatterns([...searchPatterns, emptyPattern()]);
  };

  const removePattern = (index: number): void => {
    const updated = [...searchPatterns];
    updated.splice(index, 1);
    setSearchPatterns(updated.length ? updated : []);
  };

  const updatePattern = (index: number, property: keyof GroupRuleSearchPattern, value: string | boolean | number): void => {
    const updated = [...searchPatterns];
    updated[index] = { ...updated[index], [property]: value };
    setSearchPatterns(updated);
  };

  const save = async (): Promise<void> => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (validationErrors.length) {
      return;
    }

    const toSave: GroupRuleRequest = {
      name: name.trim(),
      emailAccountId: emailAccountId as number,
      groupId: groupId as number,
      precedence: existingGroupRule ? existingGroupRule.precedence : 0,
      mustMatchAllPatterns,
      fromAddresses: fromAddresses.split(";").map((addr) => addr.trim()).filter((addr) => addr),
      searchPatterns: searchPatterns.filter((pattern) => pattern.pattern.trim().length > 0).map((pattern) => ({
        isCaseSensitive: pattern.isCaseSensitive,
        isRegex: pattern.isRegex,
        pattern: pattern.pattern.trim(),
        searchPatternType: Number(pattern.searchPatternType) as GroupRuleSearchPatternType,
      })),
    };

    if (existingGroupRule) {
      return putSetting<GroupRuleRequest>("grouprules", existingGroupRule.groupRuleId, toSave)
        .then((result: ResponseData<GroupRuleRequest>) => {
          handleSaveResponse(result);
        });
    } else {
      return postSetting<GroupRuleRequest>("grouprules", toSave)
        .then((result: ResponseData<GroupRuleRequest>) => {
          handleSaveResponse(result);
        });
    }
  };

  if (redirect) {
    return <Redirect to="/system-settings/grouprules/list" />;
  }

  return (
    <>
      <SettingsTitle errors={errors} title={existingGroupRule ? "Edit Group Rule" : "Create Group Rule"}>
        <SettingsSubmitButton onSave={save} />
      </SettingsTitle>
      <div className="row">
        <SettingsFormInfo existingTitle={existingGroupRule?.emailAccount.emailAddress}>
          {existingGroupRule && <p>Group: {existingGroupRule.group.businessName}</p>}
          {existingGroupRule && <p>Precedence: {existingGroupRule.precedence}</p>}
        </SettingsFormInfo>
        
        <div className="col-sm-7">
          <div className="content-container p-3">
            <TextInput
              label="Name"
              name="name"
              onChange={setName}
              value={name}
            />
            <SelectInput
              label="Polling Address"
              name="email-account-id"
              onChange={setEmailAccountId}
              id={emailAccountId}
              options={emailAccountOptions}
              nullOption="Select Email Account"
            />
            <div>Note: GroupRules are not supported for Groups with &quot;Classify Emails&quot;, &quot;Extract Transfer Form Data&quot; or &quot;Studio API Version&quot; = 1</div>
            <SelectInput
              label="Group"
              name="group-id"
              onChange={setGroupId}
              id={groupId}
              options={groupOptions}
              nullOption="Select Group"
            />
            
            <ToggleInput
              label="Must Match All"
              name="must-match-all-patterns"
              onChange={setMustMatchAllPatterns}
              value={mustMatchAllPatterns}
            />

            <TextareaInput
              rows={5}
              label="From Addresses separated by semicolons, to match entire domain use *@domain.com"
              name="from-addresses"
              onChange={setFromAddresses}
              value={fromAddresses}
            />

            <GroupRuleSearchPatterns
              addPattern={addPattern}
              removePattern={removePattern}
              searchPatterns={searchPatterns}
              updatePattern={updatePattern}
            />
          </div>
        </div >
      </div >
    </>
  );
};

export default GroupRuleForm;
